import { Component, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { KnowledgeBaseService } from '../../services/knowledge-base.service';
import {
  escapePlainTextForHtml,
  parseAndSanitizeMarkdown,
} from 'app/_utils/safe-markdown';
import {
  KnowledgeBaseMessage,
  AgentResponse,
  ConversationContext
} from '../../models/knowledge-base.models';
import { KNOWLEDGE_BASE_CONFIG } from '../../config/knowledge-base.config';
import { CompanyService } from '../../../_services/company.service';
import { AuthenticationService } from '../../../_services/authentication.service';
import { Company } from '../../../_models/company.model';

@Component({
  selector: 'app-knowledge-base-widget',
  templateUrl: './knowledge-base-widget.component.html',
  styleUrls: ['./knowledge-base-widget.component.scss']
})
export class KnowledgeBaseWidgetComponent implements OnInit, OnDestroy {
  @Output() widgetClosed = new EventEmitter<void>();

  messages: KnowledgeBaseMessage[] = [];
  userInput = '';
  isLoading = false;
  isTestingConnection = false;
  sessionId: string;
  
  // Configuration
  // AWS infrastructure identifiers (region, KB ID, model ARN, agent runtime ARN)
  // are all controlled by the backend — the client only sends the user query
  // and topic/tenant filters.
  tenantId = 'tenant_demo';

  // Companies for tenant selection
  companies: Company[] = [];
  selectedCompanyId: string = '';

  // Topic filtering
  topic = '';
  speakerTopic = '';
  attendeeTopic = '';

  // Search parameters
  numberOfResults = 10;
  score = 0.3;

  // UI state
  showSettings = false;
  debugMode = false;
  connectionTestResult: string | null = null;

  // Available options
  availableTopics = KNOWLEDGE_BASE_CONFIG.AVAILABLE_TOPICS;
  
  // Conversation context
  conversationContext: ConversationContext[] = [];

  constructor(
    private knowledgeBaseService: KnowledgeBaseService,
    private sanitizer: DomSanitizer,
    private companyService: CompanyService,
    private authenticationService: AuthenticationService
  ) {
    this.sessionId = this.knowledgeBaseService.generateSessionId();
  }

  ngOnInit() {
    // Load saved state from localStorage if needed
    this.loadState();
    // Always default company/tenant to the logged-in user's current company.
    // This intentionally overrides any persisted company selection in localStorage.
    this.applyCurrentCompanyDefault();
    
    // Load companies for tenant selection
    this.loadCompanies();
    
    // Show welcome message
    if (this.messages.length === 0) {
      this.messages.push({
        role: 'assistant',
        content: '👋 Hello! I\'m your Knowledge Base assistant. How can I help you today?',
        timestamp: new Date()
      });
    }
    
    setTimeout(() => this.scrollToBottom(), 100);
  }
  
  loadCompanies(): void {
    this.companyService.list().subscribe({
      next: (companies) => {
        this.companies = companies;
        
        // Restore selected company from saved state or tenantId
        if (this.selectedCompanyId) {
          const matchingCompany = companies.find(c => c.id === this.selectedCompanyId);
          if (matchingCompany) {
            this.tenantId = matchingCompany.id;
          } else {
            // If saved company ID doesn't exist, clear selection
            this.selectedCompanyId = '';
          }
        } else if (this.tenantId && this.tenantId !== 'tenant_demo') {
          // Try to match by tenantId
          const matchingCompany = companies.find(c => c.id === this.tenantId);
          if (matchingCompany) {
            this.selectedCompanyId = matchingCompany.id;
          }
        }
        
        // If still no company selected and companies are available, default to first
        if (!this.selectedCompanyId && companies.length > 0) {
          this.selectedCompanyId = companies[0].id;
          this.tenantId = companies[0].id;
          this.saveState();
        }
      },
      error: (error) => {
        console.error('Error loading companies:', error);
      }
    });
  }

  private applyCurrentCompanyDefault(): void {
    const currentCompanyId = this.authenticationService.current_company_id();
    if (currentCompanyId) {
      this.selectedCompanyId = currentCompanyId;
      this.tenantId = currentCompanyId;
      this.saveState();
    }
  }
  
  onCompanyChange(): void {
    if (this.selectedCompanyId) {
      const previousTenant = this.tenantId;
      this.tenantId = this.selectedCompanyId;
      
      // Clear session when tenant changes
      if (previousTenant && previousTenant !== this.tenantId) {
        this.newSession();
      }
      this.saveState();
    }
  }

  ngOnDestroy() {
    this.saveState();
  }

  private loadState(): void {
    try {
      const saved = localStorage.getItem('knowledgeBaseState');
      if (saved) {
        const state = JSON.parse(saved);
        if (state.messages) this.messages = state.messages.map((m: any) => ({
          ...m,
          timestamp: new Date(m.timestamp)
        }));
        if (state.sessionId) this.sessionId = state.sessionId;
        if (state.tenantId) this.tenantId = state.tenantId;
        if (state.selectedCompanyId) this.selectedCompanyId = state.selectedCompanyId;
        if (state.conversationContext) this.conversationContext = state.conversationContext;
      }
    } catch (error) {
      console.error('Error loading state:', error);
    }
  }

  private saveState(): void {
    try {
      const state = {
        messages: this.messages,
        sessionId: this.sessionId,
        tenantId: this.tenantId,
        selectedCompanyId: this.selectedCompanyId,
        conversationContext: this.conversationContext
      };
      localStorage.setItem('knowledgeBaseState', JSON.stringify(state));
    } catch (error) {
      console.error('Error saving state:', error);
    }
  }

  onTenantChange(): void {
    // This method is kept for backward compatibility but now handled by onCompanyChange
    // Clear session when tenant changes
    const previousTenant = localStorage.getItem('lastTenantId');
    if (previousTenant && previousTenant !== this.tenantId) {
      this.newSession();
    }
    localStorage.setItem('lastTenantId', this.tenantId);
  }

  getSelectedCompanyName(): string {
    if (this.selectedCompanyId && this.companies.length > 0) {
      const company = this.companies.find(c => c.id === this.selectedCompanyId);
      return company ? company.name : this.tenantId;
    }
    return this.tenantId || 'Not selected';
  }

  getMetadataPreview(): Record<string, any> {
    const metadata: Record<string, any> = {
      tenantId: this.tenantId.trim()
    };

    if (this.topic && this.topic.trim()) {
      metadata.topic = this.topic.trim();
    }

    if (this.speakerTopic && this.speakerTopic.trim()) {
      metadata.speaker_topics = [this.speakerTopic.trim()];
    }

    if (this.attendeeTopic && this.attendeeTopic.trim()) {
      metadata.attendee_topics = [this.attendeeTopic.trim()];
    }

    return metadata;
  }

  async testConnection(): Promise<void> {
    if (!this.tenantId.trim()) {
      this.connectionTestResult = '❌ Tenant ID is required';
      return;
    }

    this.isTestingConnection = true;
    this.connectionTestResult = null;

    try {
      const testParams = {
        sessionId: this.knowledgeBaseService.generateSessionId(),
        userQuestion: 'Hello',
        tenantId: this.tenantId,
        numberOfResults: 1,
        score: 0.3
      };

      const response = await this.knowledgeBaseService.queryKnowledgeBase(testParams);
      this.connectionTestResult = '✅ Connection successful!';
      if (response.answer) {
        const preview = response.answer.substring(0, 100);
        this.connectionTestResult += `\nTest response: ${preview}${response.answer.length > 100 ? '...' : ''}`;
      }
    } catch (error) {
      this.connectionTestResult = `❌ Connection test failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
    } finally {
      this.isTestingConnection = false;
    }
  }

  newSession(): void {
    this.sessionId = this.knowledgeBaseService.generateSessionId();
    this.messages = [];
    this.conversationContext = [];
    this.messages.push({
      role: 'assistant',
      content: '🆕 New session started. How can I help you?',
      timestamp: new Date()
    });
    this.saveState();
  }

  clearChat(): void {
    this.messages = [];
    this.conversationContext = [];
    this.messages.push({
      role: 'assistant',
      content: '🗑️ Chat cleared! How can I help you?',
      timestamp: new Date()
    });
    this.saveState();
  }

  async sendMessage(): Promise<void> {
    if (!this.userInput.trim() || this.isLoading) {
      return;
    }

    // Validation
    if (!this.tenantId.trim()) {
      this.messages.push({
        role: 'assistant',
        content: '❌ Error: Tenant ID is required. Please set it in the configuration panel.',
        timestamp: new Date()
      });
      return;
    }

    // Add user message
    const userMessage: KnowledgeBaseMessage = {
      role: 'user',
      content: this.userInput,
      timestamp: new Date()
    };
    this.messages.push(userMessage);

    const question = this.userInput;
    this.userInput = '';
    this.isLoading = true;

    setTimeout(() => this.scrollToBottom(), 100);

    try {
      const response = await this.knowledgeBaseService.queryKnowledgeBase({
        sessionId: this.sessionId,
        userQuestion: question,
        tenantId: this.tenantId,
        topic: this.topic,
        numberOfResults: this.numberOfResults,
        score: this.score,
        conversationContext: this.conversationContext,
        speakerTopics: this.speakerTopic ? [this.speakerTopic] : [],
        attendeeTopics: this.attendeeTopic ? [this.attendeeTopic] : []
      });

      // Add assistant response
      const assistantMessage: KnowledgeBaseMessage = {
        role: 'assistant',
        content: response.answer,
        sources: response.sources,
        timestamp: new Date()
      };
      this.messages.push(assistantMessage);

      // Update conversation context
      this.conversationContext.push({
        user: question,
        assistant: response,
        timestamp: new Date().toISOString()
      });

      // Keep only last 10 exchanges
      if (this.conversationContext.length > 10) {
        this.conversationContext = this.conversationContext.slice(-10);
      }
    } catch (error) {
      console.error('Error querying knowledge base:', error);

      const errorMessage: KnowledgeBaseMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        timestamp: new Date()
      };
      this.messages.push(errorMessage);
    } finally {
      this.isLoading = false;
      this.saveState();
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  closeWidget(): void {
    this.widgetClosed.emit();
  }

  private scrollToBottom(): void {
    const messagesContainer = document.querySelector('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }

  renderMarkdown(content: string, role: 'user' | 'assistant'): SafeHtml {
    if (role === 'assistant') {
      try {
        const sanitized = parseAndSanitizeMarkdown(content);
        return this.sanitizer.bypassSecurityTrustHtml(sanitized);
      } catch (error) {
        console.error('Error parsing markdown:', error);
        return this.sanitizer.bypassSecurityTrustHtml(escapePlainTextForHtml(content));
      }
    }
    return this.sanitizer.bypassSecurityTrustHtml(escapePlainTextForHtml(content));
  }

  toggleSettings(): void {
    this.showSettings = !this.showSettings;
  }
}

